import type {
	EnvironmentInstanceSize,
	InfraStartFailure,
	InfraStartFailureCategory,
} from "@/lib/api";

/** 表示順。待っても解消しないクォータ超過を先頭に置く。 */
export const START_FAILURE_CATEGORIES: readonly InfraStartFailureCategory[] = [
	"quota",
	"stockout",
	"otherCapacity",
	"other",
];

export const START_FAILURE_CATEGORY_LABELS: Record<InfraStartFailureCategory, string> = {
	quota: "クォータ超過",
	stockout: "AWS の在庫切れ",
	otherCapacity: "その他の EC2 エラー",
	other: "EC2 以外の起動失敗",
};

export const START_FAILURE_CATEGORY_COLORS: Record<InfraStartFailureCategory, string> = {
	quota: "var(--danger)",
	stockout: "var(--warning)",
	otherCapacity: "var(--accent)",
	other: "var(--fg-4)",
};

export type CategoryCounts = Record<InfraStartFailureCategory, number>;

function emptyCounts(): CategoryCounts {
	return { quota: 0, stockout: 0, otherCapacity: 0, other: 0 };
}

export interface CategorySummary {
	count: number;
	environmentCount: number;
}

/** 分類ごとの件数と、その分類で起動できなかった環境の数。 */
export function summarizeByCategory(
	entries: readonly InfraStartFailure[],
): Record<InfraStartFailureCategory, CategorySummary> {
	const environments = new Map<InfraStartFailureCategory, Set<string>>();
	const counts = emptyCounts();
	for (const entry of entries) {
		counts[entry.category] += 1;
		const ids = environments.get(entry.category) ?? new Set<string>();
		ids.add(entry.environmentId);
		environments.set(entry.category, ids);
	}
	return Object.fromEntries(
		START_FAILURE_CATEGORIES.map((category) => [
			category,
			{ count: counts[category], environmentCount: environments.get(category)?.size ?? 0 },
		]),
	) as Record<InfraStartFailureCategory, CategorySummary>;
}

export interface DailyCounts {
	/** ローカル時刻の日付 (YYYY-MM-DD)。 */
	date: string;
	counts: CategoryCounts;
	total: number;
}

function localDateKey(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${date.getFullYear()}-${month}-${day}`;
}

/** from から to までの毎日 (ローカル時刻) の分類別件数。件数の無い日も 0 で並べる。 */
export function dailyCounts(
	entries: readonly InfraStartFailure[],
	from: Date,
	to: Date,
): DailyCounts[] {
	const days: DailyCounts[] = [];
	const byDate = new Map<string, DailyCounts>();
	const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
	while (cursor <= to) {
		const day = { date: localDateKey(cursor), counts: emptyCounts(), total: 0 };
		days.push(day);
		byDate.set(day.date, day);
		cursor.setDate(cursor.getDate() + 1);
	}
	for (const entry of entries) {
		const day = byDate.get(localDateKey(new Date(entry.occurredAt)));
		if (day === undefined) continue;
		day.counts[entry.category] += 1;
		day.total += 1;
	}
	return days;
}

export interface InstanceBreakdownRow {
	instanceSize: EnvironmentInstanceSize;
	instanceType: string | null;
	counts: CategoryCounts;
	total: number;
}

/** インスタンス (サイズと EC2 型の組) ごとの分類別件数。件数の多い順。 */
export function breakdownByInstance(entries: readonly InfraStartFailure[]): InstanceBreakdownRow[] {
	const rows = new Map<string, InstanceBreakdownRow>();
	for (const entry of entries) {
		const key = `${entry.instanceSize}\u0000${entry.instanceType ?? ""}`;
		const row = rows.get(key) ?? {
			instanceSize: entry.instanceSize,
			instanceType: entry.instanceType,
			counts: emptyCounts(),
			total: 0,
		};
		row.counts[entry.category] += 1;
		row.total += 1;
		rows.set(key, row);
	}
	return [...rows.values()].sort(
		(a, b) => b.total - a.total || a.instanceSize.localeCompare(b.instanceSize),
	);
}
